import './lib/nativePushBridge'; // CRÍTICO: define window.setNativePushToken o mais cedo possível
import { initGlobalHaptics } from './lib/haptic';
initGlobalHaptics();

// Silenciar console.log/warn/info em produção para não vazar dados sensíveis
// e reduzir overhead de string formatting no dispositivo do usuário.
if (import.meta.env.PROD) {
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.warn = noop;
  console.debug = noop;
  // Mantém console.error para capturar falhas reais em produção
}
import React from 'react'
import ReactDOM from 'react-dom/client'
import { ThemeProvider, CssBaseline } from '@mui/material'
import InAppBrowserWarning from './components/InAppBrowserWarning.jsx'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import { shouldShowBrowserWarning } from './utils/browserDetection.js'
import muiTheme from './theme/muiTheme.js'
import { registerSW } from 'virtual:pwa-register'
import './theme.css'

// 🔥 VERSÃO DA APLICAÇÃO - injetada pelo Vite via define block (vite.config.js)
// Vem de package.json + git short hash + data do build (ex: "1.1.0+abc1234.2026-04-25")
// Cada build/deploy gera uma versão única, e o version.json é gerado em sync
import { APP_VERSION } from './version.js';
export { APP_VERSION };

// 🔥 VERSÃO MÍNIMA OBRIGATÓRIA DO SERVICE WORKER
const REQUIRED_SW_VERSION = '7.0.0';

// 🤖 FIX: Tela preta no WebView (Android e iOS) ao voltar de app externo (Instagram, WhatsApp, etc.)
;(function fixWebViewBlackScreen() {
  const isNative = !!(window.ReactNativeWebView);
  if (!isNative) return;

  function forceRepaint() {
    requestAnimationFrame(() => {
      document.body.style.opacity = '0.999';
      requestAnimationFrame(() => {
        document.body.style.opacity = '';
      });
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') forceRepaint();
  });

  window.addEventListener('focus', forceRepaint);
  window.addEventListener('pageshow', forceRepaint);
})();

// 🛡️ FORÇAR ATUALIZAÇÃO SE VERSÃO MUDOU
(function checkAppVersion() {
  try {
    const storedVersion = localStorage.getItem('APP_VERSION');
    
    if (storedVersion !== APP_VERSION) {
      console.log(`🔄 Nova versão detectada! Antiga: ${storedVersion}, Nova: ${APP_VERSION}`);
      console.log('🧹 Limpando cache antigo...');
      
      // Limpar localStorage (exceto dados críticos do Firebase Auth)
      const firebaseKeys = Object.keys(localStorage).filter(k => 
        k.startsWith('firebase:') || k.includes('firebaseLocalStorage')
      );
      const criticalData = {};
      firebaseKeys.forEach(key => {
        criticalData[key] = localStorage.getItem(key);
      });
      
      localStorage.clear();
      
      // Restaurar dados críticos do Firebase
      Object.keys(criticalData).forEach(key => {
        localStorage.setItem(key, criticalData[key]);
      });
      
      // Atualizar versão
      localStorage.setItem('APP_VERSION', APP_VERSION);
      
      console.log('✅ Cache limpo! Versão atualizada para:', APP_VERSION);
    } else {
      console.log('✅ Aplicação na versão mais recente:', APP_VERSION);
    }
  } catch (err) {
    console.warn('⚠️ Erro ao verificar versão:', err);
  }
})();

// 🛟 AUTO-CURA: chunk falhou ao carregar (deploy novo apagou chunk antigo do cache)
// O Vite dispara 'vite:preloadError' quando qualquer import() dinâmico falha —
// recarrega pra buscar o index.html novo em vez de mostrar a tela de erro.
// Usa o mesmo __chunkRetryTime do loadAppWithRetry: no máximo 1 reload a cada 8s.
window.addEventListener('vite:preloadError', (event) => {
  const lastRetry = parseInt(sessionStorage.getItem('__chunkRetryTime') || '0');
  if (Date.now() - lastRetry <= 8000) return; // deixa cair no ErrorBoundary — evita loop de reload
  sessionStorage.setItem('__chunkRetryTime', String(Date.now()));
  event.preventDefault(); // não deixa o erro estourar no ErrorBoundary
  window.location.reload();
});

// 🚀 Função para forçar atualização do Service Worker
async function enforceServiceWorkerVersion() {
  if (!('serviceWorker' in navigator)) {
    return; // Navegador não suporta SW
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    
    if (!registration) {
      console.log('✅ Nenhum Service Worker registrado ainda');
      return;
    }

    // Verificar versão atual ouvindo mensagem do SW
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data.type === 'SW_UPDATED') {
        const currentVersion = event.data.version;
        console.log(`📦 Service Worker versão: ${currentVersion}`);
        
        // Comparar versões
        if (currentVersion !== REQUIRED_SW_VERSION) {
          console.warn(`⚠️ Service Worker desatualizado! Atual: ${currentVersion}, Requerido: ${REQUIRED_SW_VERSION}`);
          forceUpdate();
        }
      }
    });

    // Forçar verificação de atualização
    console.log('🔄 Verificando atualizações do Service Worker...');
    await registration.update();

    // Se há SW esperando, ativar imediatamente
    if (registration.waiting) {
      console.log('🔄 Nova versão do Service Worker detectada - ativando...');
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      forceUpdate();
    }

    // Se há SW instalando, aguardar e ativar
    if (registration.installing) {
      registration.installing.addEventListener('statechange', (e) => {
        if (e.target.state === 'installed' && navigator.serviceWorker.controller) {
          console.log('✅ Nova versão instalada - recarregando...');
          forceUpdate();
        }
      });
    }

  } catch (error) {
    console.error('❌ Erro ao verificar Service Worker:', error);
  }
}

// 🔄 Forçar reload da página para aplicar nova versão
function forceUpdate() {
  console.log('🔄 Forçando atualização da aplicação...');
  
  // Limpar todos os caches
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        if (!name.includes(REQUIRED_SW_VERSION)) {
          console.log(`🗑️ Removendo cache antigo: ${name}`);
          caches.delete(name);
        }
      });
    });
  }
  
  // Recarregar página (hard refresh)
  setTimeout(() => {
    window.location.reload(true);
  }, 1000);
}

// 🚀 VITE PWA - Registro automático do Service Worker (com proteção para dispositivos antigos)
if ('serviceWorker' in navigator) {
  try {
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        console.log('🔄 Nova versão disponível — aplicando na próxima oportunidade...');

        function applyUpdate() {
          try { updateSW(true); } catch (_) { window.location.reload(); }
        }

        // Se já está em background, aplica em 3s
        if (document.hidden) {
          setTimeout(applyUpdate, 3000);
          return;
        }

        // Aplica quando o usuário sair da aba (próxima vez que minimizar/trocar)
        function onHidden() {
          if (document.visibilityState === 'hidden') {
            document.removeEventListener('visibilitychange', onHidden);
            setTimeout(applyUpdate, 1000);
          }
        }
        document.addEventListener('visibilitychange', onHidden);

        // Fallback: aplica após 5 minutos mesmo que ainda visível
        setTimeout(() => {
          document.removeEventListener('visibilitychange', onHidden);
          applyUpdate();
        }, 5 * 60 * 1000);
      },
      onOfflineReady() {
        console.log('✅ App pronto para funcionar offline');
      },
      onRegisteredSW(swScriptUrl, registration) {
        console.log('✅ Service Worker registrado:', swScriptUrl);
        
        // Verificar atualizações a cada 5 minutos (era 30s — causava reloads frequentes no Android)
        setInterval(() => {
          try {
            registration.update().catch(() => {});
          } catch (e) {
            // Ignora erros silenciosamente
          }
        }, 5 * 60 * 1000);

        // Polling de version.json a cada 2 minutos — força iOS a detectar novo deploy
        setInterval(async () => {
          try {
            const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
            if (!res.ok) return;
            const data = await res.json();
            const remoteVersion = data?.version;
            if (remoteVersion && remoteVersion !== APP_VERSION) {
              console.log(`🔄 [VERSION POLL] Nova versão no servidor: ${remoteVersion} (local: ${APP_VERSION}) — recarregando...`);
              // Limpa caches e recarrega
              if ('caches' in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map(k => caches.delete(k)));
              }
              window.location.reload(true);
            }
          } catch { /* silencioso */ }
        }, 2 * 60 * 1000);
      },
      onRegisterError(error) {
        console.warn('⚠️ Service Worker não suportado neste dispositivo:', error);
        // Continua sem SW - app funciona mesmo assim
      }
    });
  } catch (swError) {
    console.warn('⚠️ Erro ao configurar Service Worker - continuando sem cache offline:', swError);
    // App continua funcionando sem Service Worker
  }
} else {
  console.log('ℹ️ Service Worker não suportado neste navegador');
}

// O SW unificado (src/sw.js) é registrado pelo registerSW acima.
// Ele contém: Workbox precaching + runtime caching + push handler.
// NÃO registrar nenhum outro SW — um único SW no scope '/' resolve
// o conflito que impedia push notifications no Android PWA.

const rootElement = document.getElementById('root');
const root = ReactDOM.createRoot(rootElement);

// Função para esconder splash screen
// Esconde IMEDIATAMENTE quando o app fica pronto — sem duração mínima
// A animação CSS toca em background, mas o app aparece na hora
function hideSplash() {
  if (window.__splashTimeout) clearTimeout(window.__splashTimeout);
  if (window.__splashTimeout2) clearTimeout(window.__splashTimeout2);

  const splash = document.getElementById('splash-screen');
  const root = document.getElementById('root');
  if (splash) {
    splash.classList.add('hidden');
    if (root) root.classList.add('app-visible');
    setTimeout(() => {
      try {
        if (splash.parentNode) splash.remove();
      } catch (_) {
        // Ignorar erros de remoção da splash (race condition com React)
      }
    }, 500);
  }
}
window.__hideSplash = hideSplash;

if (shouldShowBrowserWarning()) {
  // console.warn('⚠️ Navegador in-app detectado (Instagram/Facebook) - mostrando aviso SEM carregar Firebase');
  
  root.render(
    <ErrorBoundary>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <InAppBrowserWarning />
      </ThemeProvider>
    </ErrorBoundary>
  );
  hideSplash();
} else {
  // console.log('✅ Navegador normal detectado - carregando app completo com Firebase');
  
  // Tenta importar o app — com retry automático se chunk falhar
  // (típico quando Vite rebuilda dependências e URLs antigas viram 404)
  function loadAppWithRetry(attempt = 1) {
    return import('./MainAppLoader.jsx').catch((error) => {
      // Serializa erro de verdade (chunk-load errors costumam vir como {} vazio)
      const msg = (error && (error.message || error.toString())) || 'unknown';
      const isChunkError = /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError/i.test(msg);
      
      console.error(`❌ Erro ao carregar aplicação (tentativa ${attempt}):`, msg, error);
      
      // Chunk error → recarrega automaticamente, mas só 1x a cada 8s
      // (usa timestamp em vez de flag booleana pra sobreviver a reloads do version-check)
      const lastRetry = parseInt(sessionStorage.getItem('__chunkRetryTime') || '0');
      const msSinceLastRetry = Date.now() - lastRetry;
      if (isChunkError && msSinceLastRetry > 8000) {
        sessionStorage.setItem('__chunkRetryTime', String(Date.now()));
        console.warn('🔄 Detectado chunk antigo — recarregando com cache limpo...');
        // Marca que vai recarregar — o catch externo manterá o splash visível
        error.__willReload = true;
        setTimeout(() => window.location.reload(), 500);
        throw error;
      }
      
      throw error;
    });
  }
  
  loadAppWithRetry()
    .then(({ default: MainAppLoader }) => {
      // Sucesso → limpa timestamp de retry
      sessionStorage.removeItem('__chunkRetryTime');
      root.render(
        <ErrorBoundary>
          <ThemeProvider theme={muiTheme}>
            <CssBaseline />
            <MainAppLoader />
          </ThemeProvider>
        </ErrorBoundary>
      );
      hideSplash();
    })
    .catch((error) => {
      // Se vai recarregar automaticamente → mantém o splash visível, não pisca erro
      if (error && error.__willReload) return;

      const msg = (error && (error.message || error.toString())) || 'Erro desconhecido';
      hideSplash();
      root.render(
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#1a1a1a',
          color: '#ff5252',
          padding: '20px',
          textAlign: 'center',
          fontFamily: 'sans-serif'
        }}>
          <div style={{ maxWidth: 480 }}>
            <h2>Erro ao Carregar</h2>
            <p>Ocorreu um erro ao carregar a aplicação.</p>
            <p style={{ fontSize: 12, opacity: 0.7, marginTop: 12, wordBreak: 'break-word' }}>
              {msg}
            </p>
            <button 
              onClick={() => {
                sessionStorage.removeItem('__chunkRetryTime');
                window.location.reload();
              }}
              style={{
                marginTop: '20px',
                padding: '12px 24px',
                background: '#F0B90B',
                color: '#000',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: 14
              }}
            >
              Recarregar
            </button>
          </div>
        </div>
      );
    });
}
